/**
 * Job Details → Stage Management.
 * Organised like SourcingTab: gray page surface, title block, white cards.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GripVertical, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';
import { Skeleton } from '@/components/ui/atoms/Skeleton';
import { Typography } from '@/components/ui/atoms/Typography';
import { useJobCandidates, useJobPipeline } from '@/hooks/useJobOffers';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';
import {
  getCandidateStageCounts,
  sortStagesByPosition,
} from '@/utils/pipeline-stages';
import { CreatePipelineStageDialog } from '../CreatePipelineStageDialog';
import { StageManagementAboutCard } from './StageManagementAboutCard';
import { DeleteStageDialog } from './DeleteStageDialog';
import { StageManagementRow } from './StageManagementRow';
import { useStageManagementActions } from './useStageManagementActions';

export interface StageManagementTabProps {
  jobId: string;
}

export function StageManagementTab({ jobId }: StageManagementTabProps) {
  const { t } = useTranslation(['job', 'candidates', 'common']);
  const getStageLabel = useStageDisplayLabel();
  const { data: pipeline, isLoading: isPipelineLoading } = useJobPipeline(jobId);
  const { data: candidates } = useJobCandidates(jobId);

  const stages = useMemo(
    () => sortStagesByPosition(pipeline?.stages),
    [pipeline?.stages]
  );
  const candidateCounts = useMemo(
    () => getCandidateStageCounts(candidates ?? [], stages),
    [candidates, stages]
  );

  const {
    stageIds,
    renamingStageId,
    isCreating,
    handleRename,
    isDeleting,
    handleDelete,
    handleCreate,
    handleReorder,
    handleAutomationChange,
  } = useStageManagementActions(jobId, stages);

  const [pendingDeleteStageId, setPendingDeleteStageId] = useState<string | null>(null);
  const pendingDeleteStage = stages.find((stage) => stage.id === pendingDeleteStageId);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLabel, setCreateLabel] = useState('');
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [overDragId, setOverDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverDragId(event.over ? String(event.over.id) : null);
  }, []);

  const clearDrag = useCallback(() => {
    setActiveDragId(null);
    setOverDragId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      clearDrag();
      if (!over || active.id === over.id) return;

      const oldIndex = stageIds.indexOf(String(active.id));
      const newIndex = stageIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;

      handleReorder(stageIds, arrayMove(stageIds, oldIndex, newIndex));
    },
    [clearDrag, handleReorder, stageIds]
  );

  const activeStage = activeDragId
    ? stages.find((stage) => stage.id === activeDragId)
    : undefined;
  const activeIndex = activeDragId ? stageIds.indexOf(activeDragId) : -1;
  const overIndex = overDragId ? stageIds.indexOf(overDragId) : -1;

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-background-gray">
      <div className="space-y-5 px-4 py-6 lg:px-8 lg:py-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-1">
            <Typography as="h2" variant="h6" color="primary">
              {t('job:stage_management.title')}
            </Typography>
            <Typography
              as="p"
              variant="body-sm"
              color="secondary"
              className="leading-relaxed md:whitespace-nowrap"
            >
              {t('job:stage_management.subtitle')}
            </Typography>
          </div>
          <Button
            type="contained"
            intent="primary"
            size="md"
            icon={<Plus />}
            label={t('job:stage_management.add_stage')}
            onClick={() => setCreateOpen(true)}
            className="shrink-0"
          />
        </header>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_24rem] xl:items-start">
          <section className="min-w-0 rounded-xl border border-light-lila/60 bg-light p-5">
            {isPipelineLoading && stages.length === 0 ? (
              <ul className="space-y-3" aria-busy="true">
                {Array.from({ length: 5 }).map((_, index) => (
                  <li key={index}>
                    <Skeleton className="h-16 w-full rounded-xl" />
                  </li>
                ))}
              </ul>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={clearDrag}
              >
                <SortableContext
                  items={stageIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul
                    className="m-0 list-none space-y-3 p-0"
                    aria-label={t('job:stage_management.list_aria')}
                  >
                    {stages.map((stage) => {
                      const showAbove =
                        Boolean(activeDragId) &&
                        overDragId === stage.id &&
                        activeIndex > overIndex &&
                        overIndex >= 0;
                      const showBelow =
                        Boolean(activeDragId) &&
                        overDragId === stage.id &&
                        activeIndex < overIndex &&
                        overIndex >= 0;
                      const count = candidateCounts[stage.id] ?? 0;

                      return (
                        <StageManagementRow
                          key={stage.id}
                          stage={stage}
                          candidateCount={count}
                          automationEnabled={stage.automationEnabled}
                          onAutomationChange={handleAutomationChange}
                          onRename={handleRename}
                          onDelete={setPendingDeleteStageId}
                          canDelete={!stage.isDefault && count === 0}
                          isRenaming={renamingStageId === stage.id}
                          showDropIndicatorAbove={showAbove}
                          showDropIndicatorBelow={showBelow}
                        />
                      );
                    })}
                  </ul>
                </SortableContext>

                <DragOverlay dropAnimation={null}>
                  {activeStage ? (
                    <div className="flex w-[min(28rem,90vw)] items-center gap-2.5 rounded-xl border border-purple/30 bg-light px-4 py-3 shadow-md">
                      <GripVertical
                        className="h-4 w-4 shrink-0 text-dark-invirtus/40"
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <Typography
                          as="p"
                          variant="body-sm"
                          weight="semibold"
                          color="primary"
                          className="truncate"
                        >
                          {getStageLabel(activeStage)}
                        </Typography>
                        <Typography
                          as="p"
                          variant="body-xs"
                          color="muted"
                          className="mt-0.5 flex items-center gap-1.5"
                        >
                          <Users className="h-3.5 w-3.5" aria-hidden />
                          {t('job:stage_management.candidates_count', {
                            count: candidateCounts[activeStage.id] ?? 0,
                          })}
                        </Typography>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </section>

          <div className="xl:sticky xl:top-4">
            <StageManagementAboutCard />
          </div>
        </div>
      </div>

      <CreatePipelineStageDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setCreateLabel('');
        }}
        label={createLabel}
        onLabelChange={setCreateLabel}
        onConfirm={(label) => {
          void handleCreate(label).then(() => {
            setCreateOpen(false);
            setCreateLabel('');
          });
        }}
        isSubmitting={isCreating}
      />

      <DeleteStageDialog
        stageLabel={pendingDeleteStage ? getStageLabel(pendingDeleteStage) : null}
        isDeleting={isDeleting}
        onCancel={() => setPendingDeleteStageId(null)}
        onConfirm={() => {
          if (!pendingDeleteStage) return;
          void handleDelete(pendingDeleteStage.id).finally(() =>
            setPendingDeleteStageId(null)
          );
        }}
      />
    </div>
  );
}

export default StageManagementTab;
